export const CAMERA_LUCIDA_CAP_GAP = 8
export const CAMERA_LUCIDA_TRAY_GAP = 12
export const CAMERA_LUCIDA_STUB_BAND = 18
export const CAMERA_LUCIDA_STUB_STEP = 16
const CAMERA_LUCIDA_STAGE_PADDING = 4
const CAMERA_LUCIDA_CONTENT_PADDING = 6
const CAMERA_LUCIDA_LIFT_RATIO = 0.38
const CAMERA_LUCIDA_MIN_STUB_SCALE = 0.4
const CAMERA_LUCIDA_MIN_STUB_BRIGHTNESS = 0.16
const CAMERA_LUCIDA_MAX_LAYOUT_VALUE = 1000000

export interface CameraLucidaVerticalLayoutInput {
  stageHeight: number
  captionHeight: number
  windowBottom: number
  stubCount: number
}

export interface CameraLucidaVerticalLayout {
  capTop: number
  trayTop: number
  stubStep: number
  trayHeight: number
  lift: number
}

export interface CameraLucidaStubPresentation {
  scale: number
  brightness: number
}

export interface CameraLucidaRowOrdinalRange {
  first: number
  last: number
}

function cameraLucidaNonNegativeInteger(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0
  return Math.min(CAMERA_LUCIDA_MAX_LAYOUT_VALUE, Math.floor(value))
}

export function computeCameraLucidaVerticalLayout(
  input: CameraLucidaVerticalLayoutInput,
): CameraLucidaVerticalLayout {
  const stageHeight = cameraLucidaNonNegativeInteger(input.stageHeight)
  const captionHeight = cameraLucidaNonNegativeInteger(input.captionHeight)
  const windowBottom = cameraLucidaNonNegativeInteger(input.windowBottom)
  const stubCount = cameraLucidaNonNegativeInteger(input.stubCount)
  const desiredCapTop = windowBottom + CAMERA_LUCIDA_CAP_GAP
  const captionReserve = stubCount > 0
    ? captionHeight + CAMERA_LUCIDA_TRAY_GAP + CAMERA_LUCIDA_STUB_BAND + CAMERA_LUCIDA_STAGE_PADDING
    : captionHeight + CAMERA_LUCIDA_CONTENT_PADDING
  const maximumCapTop = Math.max(0, stageHeight - captionReserve)
  let capTop = Math.max(0, Math.min(desiredCapTop, maximumCapTop))

  const maximumTrayTop = Math.max(0, stageHeight - CAMERA_LUCIDA_STUB_BAND - CAMERA_LUCIDA_STAGE_PADDING)
  let trayTop = stubCount > 0
    ? Math.min(capTop + captionHeight + CAMERA_LUCIDA_TRAY_GAP, maximumTrayTop)
    : capTop + captionHeight + CAMERA_LUCIDA_TRAY_GAP
  const availableStepHeight = Math.max(
    0,
    stageHeight - trayTop - CAMERA_LUCIDA_STUB_BAND - CAMERA_LUCIDA_STAGE_PADDING,
  )
  const fittedStep = stubCount > 1
    ? Math.floor(availableStepHeight / (stubCount - 1))
    : CAMERA_LUCIDA_STUB_STEP
  const stubStep = Math.max(0, Math.min(CAMERA_LUCIDA_STUB_STEP, fittedStep))
  const trayHeight = stubCount > 0
    ? (stubCount - 1) * stubStep + CAMERA_LUCIDA_STUB_BAND
    : 0
  const occupiedBottom = trayTop + trayHeight
  const spareHeight = Math.max(0, stageHeight - occupiedBottom - CAMERA_LUCIDA_CONTENT_PADDING)
  const lift = Math.max(0, Math.round(spareHeight * CAMERA_LUCIDA_LIFT_RATIO))

  capTop += lift
  trayTop += lift
  return { capTop, trayTop, stubStep, trayHeight, lift }
}

export function computeCameraLucidaStubPresentation(depth: number): CameraLucidaStubPresentation {
  const safeDepth = cameraLucidaNonNegativeInteger(depth)
  const scale = Math.max(
    CAMERA_LUCIDA_MIN_STUB_SCALE,
    Math.min(1, 1 - safeDepth * 0.006),
  )
  const brightness = Math.max(
    CAMERA_LUCIDA_MIN_STUB_BRIGHTNESS,
    Math.min(1, 1 - safeDepth * 0.016),
  )
  return { scale, brightness }
}

export function cameraLucidaRowOrdinalRange(
  ordinals: readonly number[],
): CameraLucidaRowOrdinalRange | null {
  if (ordinals.length === 0) return null
  const firstValue = ordinals[0]
  if (!Number.isFinite(firstValue)) return null
  let first = firstValue
  let last = firstValue
  for (let index = 1; index < ordinals.length; index += 1) {
    const value = ordinals[index]
    if (!Number.isFinite(value)) return null
    first = Math.min(first, value)
    last = Math.max(last, value)
  }
  return { first, last }
}

export function normalizeCameraLucidaLeafBudget(budget: number): number {
  if (!Number.isFinite(budget) || !Number.isInteger(budget) || budget <= 0) {
    throw new RangeError('Camera Lucida leaf budget must be a finite positive integer.')
  }
  return budget
}
